import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from "react-redux";
import cx from "classnames";
import InfiniteScroll from "react-infinite-scroll-component";
import Modal, { ModalBody, ModalFooter } from "Components/Modal";
import { IconButton, TextField, Typography, InputBase } from "@mui/material";
import { NearMeOutlined } from "@mui/icons-material";

import SupportChatMessages from "./SupportChatMessages";
import { close, setIsExpanded } from "Modules/chat/views/chatsContainer/redux/slice";
import { closeChat } from "../redux/supportChannelsSlice";
import { 
  cancelConversationRequest,
  restartConversation,
  startSalesforceConversation,
  resetConversation,
  // Network monitoring
  selectIsNetworkOnline,
  selectAgentName,
  selectSalesforceUserContext,
  selectConversationsCount,
  selectHasAgentJoined,
  selectAgentJoinedAt,
} from "../redux/messageSlice";
import salesforceService from '../services/salesforceService';
import { CONVERSATION_STATUS, CONNECTION_STATUS, ROUTING_ATTRIBUTES } from '../services/constants';
import { getRoutingAttributes } from '../config/salesforceConfig';
import useChatContainerHeight from "Modules/chat/hooks/useChatContainerHeight";

import "Modules/chat/index.scss";

const INPUT_MAX_LENGTH = 500;

// Height constants for container calculation
const HEADER_HEIGHT = 64;
const FOOTER_BASE_HEIGHT = 85;
const STATUS_BAR_HEIGHT = 0;
const INPUT_WITH_COUNTER_BUFFER = 50;
const INPUT_WITHOUT_COUNTER_BUFFER = 34;

// Reusable agent wait icon component - defined outside to prevent recreation on re-renders
const AgentWaitIcon = () => (
  <div className="agent-wait-icon">
    <div className="avatar-wrapper">
      <svg className="rotating-border" viewBox="0 0 140 140">
        <circle
          className="border-circle"
          cx="70"
          cy="70"
          r="65"
          fill="none"
          strokeWidth="3"
          stroke="#D0D3D5"
          strokeDasharray="372 36.5"
          strokeLinecap="round"
        />
      </svg>
      <div
        className="agent-avatar"
        role="img"
        aria-label="Support agent"
      />
    </div>
  </div>
);

const SupportMessageList = ({
  messages,
  openModal,
  onClose,
  items = [],
  chat,
  onSendMessage,
  onUpdateMessageStatus,
  onUserStatusWriting,
  authId,
  chatUsers,
  salesforceEnabled = false,
  salesforceStatus = null,
  loading = false,
  error = null,
  locale,
  routingAttributes = {},
}) => {
  const dispatch = useDispatch();
  const { isExpanded } = useSelector((state) => state.chatsContainerRedux);
  const isNetworkOnline = useSelector(selectIsNetworkOnline);
  const agentName = useSelector(selectAgentName);
  const salesforceUserContext = useSelector(selectSalesforceUserContext);
  const conversationsCount = useSelector(selectConversationsCount);
  const hasAgentJoined = useSelector(selectHasAgentJoined);
  const agentJoinedAt = useSelector(selectAgentJoinedAt);
  const [draft, setDraft] = useState("");
  // const [chatStartedLabel, setChatStartedLabel] = useState("");
  // const [itemsGrouped, setItemsGrouped] = useState({});
  // const [dateLocaleLabel, setDateLocaleLabel] = useState(DATE_LABELS);
  const [userIsAtBottom, setUserIsAtBottom] = useState(true);
  const [hasInitialScrolled, setHasInitialScrolled] = useState(false);

  // Show connection status for Salesforce
  const showSalesforceStatus = salesforceEnabled && salesforceStatus;

  // Check if Salesforce is ready for sending messages
  const canSendMessage = salesforceEnabled ?
    (salesforceStatus?.canSendMessage && 
     salesforceStatus?.conversationStatus === CONVERSATION_STATUS.ACTIVE && 
     salesforceStatus?.conversationStatus !== CONVERSATION_STATUS.ERROR) :
    true;

  // Calculate dynamic footer height based on conversation status
  const footerHeight = useMemo(() => {
    // When conversation is ended, footer height is 105px (larger to accommodate "Start new chat" link)
    if (salesforceStatus?.conversationStatus === CONVERSATION_STATUS.WAITING_FOR_AGENT
      || salesforceStatus?.conversationStatus === CONVERSATION_STATUS.AGENT_UNAVAILABLE
    ) {
      if(!isNetworkOnline) {
        return 135;
      }
      return 105;
    }

    if (salesforceStatus?.conversationStatus === CONVERSATION_STATUS.NO_AGENTS_FOUND) {
      if(!isNetworkOnline) {
        return 165;
      }
      return 120
    }

    return FOOTER_BASE_HEIGHT;
  }, [salesforceStatus?.conversationStatus, isNetworkOnline]);

  // Use shared height calculation hook
  const { inputRef, scrollRef, bodyRef, recalculate, showInputCharacterCounter } = useChatContainerHeight({
    headerHeight: HEADER_HEIGHT,
    footerBaseHeight: footerHeight,
    statusBarHeight: STATUS_BAR_HEIGHT,
    showStatusBar: showSalesforceStatus,
    enabled: openModal,
    minInputHeight: 15,
    inputCounterVisibleHeight: INPUT_WITH_COUNTER_BUFFER,
    inputCounterHiddenHeight: INPUT_WITHOUT_COUNTER_BUFFER,
  });

  // Memoize connection status text
  const connectionStatusText = useMemo(() => {
    if (!salesforceStatus) return '';

    if (!isNetworkOnline) {
      return messages?.support_status_offline;
    }

    switch (salesforceStatus.conversationStatus) {
      case CONVERSATION_STATUS.NOT_STARTED:
        return messages?.support_status_initializing;
      case CONVERSATION_STATUS.CONNECTING:
        return messages?.support_status_connecting;
      case CONVERSATION_STATUS.WAITING_FOR_AGENT:
        return messages?.support_status_waiting_agent;
      case CONVERSATION_STATUS.ACTIVE:
        return salesforceStatus.connectionStatus === CONNECTION_STATUS.CONNECTED ?
          (messages?.support_status_connected) :
          (messages?.support_status_reconnecting);
      case CONVERSATION_STATUS.AGENT_UNAVAILABLE:
        return messages?.support_status_agent_unavailable;
      case CONVERSATION_STATUS.ENDED:
        return messages?.support_status_ended;
      case CONVERSATION_STATUS.ERROR:
        return messages?.support_status_error;
      default:
        return '';
    }
  }, [salesforceStatus, messages, isNetworkOnline]);

  // Log items changes to debug flashing
  useEffect(() => {
    console.log('[SupportMessageList] items changed:', items, 'length:', items?.length, 'type:', typeof items, 'isNull:', items === null);
  }, [items]);

  // Mark unread messages as read when displayed
  useEffect(() => {
    if (!items || !items.length || !onUpdateMessageStatus) return;

    const unreadMessages = items
      .filter((item) => item.status !== "Read" && item.sender !== authId)
      .map((item) => item.id);

    if (unreadMessages.length) {
      onUpdateMessageStatus(unreadMessages, "Read");
    }
  }, [items, authId, onUpdateMessageStatus]);

  // Calculate chat started label based on first message, currently keep but commented out for future enhancements
  // useEffect(() => {
  //   if (!items || items.length === 0) {
  //     setChatStartedLabel("");
  //     return;
  //   }

  //   // Find the earliest message timestamp
  //   const sortedItems = [...items].sort((a, b) => {
  //     const timeA = a.timestamp || a.createdOn || 0;
  //     const timeB = b.timestamp || b.createdOn || 0;
  //     return timeA - timeB;
  //   });
  //   const firstMessage = sortedItems[0];

  //   if (firstMessage && (firstMessage.timestamp || firstMessage.createdOn)) {
  //     const startTime = firstMessage.timestamp || firstMessage.createdOn;
  //     const startMoment = moment(startTime);
  //     const isToday = startMoment.isSame(moment(), 'day');

  //     if (isToday) {
  //       setChatStartedLabel(`${messages["chat_startedAt"]} ${startMoment.format('h:mm a').replace("pm", "p.m.").replace("am", "a.m.")}`);
  //     } else {
  //       setChatStartedLabel(`${messages["chat_startedOn"]} ${startMoment.format('MMMM D, YYYY')}`);
  //     }
  //   }
  // }, [items, messages]);



  // Clear draft and blur input when conversation ends
  useEffect(() => {
    if (!salesforceEnabled || !salesforceStatus) return;

    if (salesforceStatus.conversationStatus === CONVERSATION_STATUS.ENDED) {
      setDraft("");
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  }, [salesforceEnabled, salesforceStatus?.conversationStatus]);

  // Reset initial scroll flag when modal closes
  useEffect(() => {
    if (!openModal) {
      setHasInitialScrolled(false);
    }
  }, [openModal]);

  // Check if agent has joined the conversation
  const agentHasJoined = useMemo(() => {
    if (!salesforceEnabled || !salesforceStatus) return true; // Non-Salesforce chats always allow scroll
    
    // Agent has joined when conversation is ACTIVE and there are messages
    const isActive = salesforceStatus.conversationStatus === CONVERSATION_STATUS.ACTIVE;
    const hasMessages = items && items.length > 0;
    
    return isActive && hasMessages;
  }, [salesforceEnabled, salesforceStatus?.conversationStatus, items?.length]);

  // Track user scroll position to know if they're reading history
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl || !openModal) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollEl;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      // Consider user at bottom if within 100px
      setUserIsAtBottom(distanceFromBottom < 100);
    };

    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [openModal]);

  // Recalculate heights and scroll to bottom when modal opens
  // First time opening: always scroll to bottom once
  // After initial scroll:
  //   - Agent not joined: don't auto-scroll (user can read static messages)
  //   - Agent joined: only scroll if user is at bottom
  useEffect(() => {
    if (openModal && scrollRef.current) {
      const timer = setTimeout(() => {
        recalculate();
        
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;
        
        // Use requestAnimationFrame to ensure browser has painted
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const scrollHeight = scrollEl.scrollHeight;
            const clientHeight = scrollEl.clientHeight;
            
            const canScroll = scrollHeight > clientHeight;
            
            // First time opening modal: always scroll to bottom (only if content is scrollable)
            if (!hasInitialScrolled && canScroll) {
              scrollEl.scrollTop = scrollHeight;
              setHasInitialScrolled(true);
            }
            // After initial scroll: only auto-scroll if agent has joined and user is at bottom
            else if (hasInitialScrolled && agentHasJoined && userIsAtBottom && canScroll) {
              scrollEl.scrollTop = scrollHeight;
            }
          });
        });
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [openModal, recalculate, userIsAtBottom, agentHasJoined, hasInitialScrolled]);

  // Recalculate heights and conditionally scroll when input content changes
  // Only scroll when agent has joined and user is at bottom
  useEffect(() => {
    if (openModal) {
      const timer = setTimeout(() => {
        recalculate();

        // Only scroll if agent has joined and user is at bottom (not reading history)
        if (agentHasJoined && userIsAtBottom && scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, recalculate, userIsAtBottom, agentHasJoined]);

  // Scroll to bottom when new messages arrive
  // Only scroll when agent has joined and user is at bottom
  useEffect(() => {
    if (openModal && scrollRef.current && items && items.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [items?.length, openModal]);

  // Scroll to bottom when footer height changes (e.g., network status change)
  useEffect(() => {
    if (openModal && scrollRef.current && !isNetworkOnline) {
      const timer = setTimeout(() => {
        recalculate();
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [footerHeight, openModal, recalculate]);

  // Memoize event handlers
  const handleSubmitMessage = useCallback(() => {
    const trimmed = draft.trim();
    if (!trimmed || !onSendMessage || !chat?.id || !canSendMessage) {
      return;
    }

    const messageData = {
      message: trimmed,
      sender: authId,
      chatId: chat.id,
      createdOn: new Date().toISOString(), // Use ISO string format for moment.js compatibility
      timestamp: Date.now(),
      status: salesforceEnabled ? "sending" : "Sent",
      type: salesforceEnabled ? "salesforce" : "regular",
    };

    onSendMessage(messageData);
    setDraft("");
  }, [draft, onSendMessage, chat?.id, canSendMessage, authId, salesforceEnabled]);

  const handleTyping = useCallback((event) => {
    const { value } = event.target;
    setDraft(value);

    if (onUserStatusWriting && chat?.id) {
      onUserStatusWriting(chat.id, authId, value.length > 0);
    }
  }, [onUserStatusWriting, chat?.id, authId]);

  const isDisableSendMessage = () => {
    return !isNetworkOnline || !canSendMessage || loading || !draft?.trim() || draft?.length > INPUT_MAX_LENGTH;
  }

  const isDisableChatBox = () => {
    const shouldDisable = !isNetworkOnline || !canSendMessage || loading || (salesforceEnabled && (
      salesforceStatus?.conversationStatus === CONVERSATION_STATUS.ERROR ||
      salesforceStatus?.conversationStatus === CONVERSATION_STATUS.WAITING_FOR_AGENT ||
      salesforceStatus?.conversationStatus === CONVERSATION_STATUS.NOT_STARTED ||
      salesforceStatus?.conversationStatus === CONVERSATION_STATUS.CONNECTING
    ));

    return shouldDisable;
  }

  // Memoize input placeholder
  const inputPlaceholder = useMemo(() => {
    if (!salesforceEnabled) {
      return messages?.FSM_SETTINGS_MESSAGES_WRITE_MESSAGE;
    }

    if (!salesforceStatus) {
      return messages?.support_input_initializing;
    }

    if (!isNetworkOnline) {
      return messages?.support_network_error;
    }

    switch (salesforceStatus.conversationStatus) {
      case CONVERSATION_STATUS.NOT_STARTED:
        return messages?.support_input_initializing;
      case CONVERSATION_STATUS.CONNECTING:
        return messages?.support_input_connecting;
      case CONVERSATION_STATUS.WAITING_FOR_AGENT:
        return messages?.support_input_waiting;
      case CONVERSATION_STATUS.ACTIVE:
        return salesforceStatus.connectionStatus === CONNECTION_STATUS.CONNECTED ?
          (messages?.FSM_SETTINGS_MESSAGES_WRITE_MESSAGE) :
          (messages?.support_status_reconnecting);
      case CONVERSATION_STATUS.AGENT_UNAVAILABLE:
        return messages?.support_input_unavailable;
      case CONVERSATION_STATUS.ENDED:
        return messages?.support_input_ended;
      case CONVERSATION_STATUS.ERROR:
        return messages?.support_input_error;
      default:
        return messages?.support_input_unavailable_default;
    }
  }, [salesforceEnabled, salesforceStatus, messages]);

  // Toggle expand/collapse state
  const handleToggle = useCallback(() => {
    dispatch(setIsExpanded(!isExpanded));
  }, [dispatch, isExpanded]);

  // Back button - navigate back to chat list (preserve conversation state)
  const handleBack = useCallback(() => {
    if (salesforceEnabled) {
      salesforceService.hideChat(); // Just hide, preserve state
    }
    // Navigate back to chat list instead of closing modal
    dispatch(closeChat()); // Sets activeChatType = null
    // Pass isClosingViaBackButton=true to preserve conversation state
    onClose?.(true); // true = closing via back button, preserve state
  }, [salesforceEnabled, dispatch, onClose]);

  // Collapse button - preserve conversation state  
  const handleCollapse = useCallback(() => {
    if (salesforceEnabled) {
      salesforceService.hideChat(); // Just hide, preserve state
    }
    handleToggle(); // Also toggle the expand state
  }, [salesforceEnabled, handleToggle]);

  // X button - close modal and clear state for ENDED/ERROR conversations
  const handleClose = useCallback(async () => {
    // Pass isClosingViaBackButton=false to indicate this is X button (not back button)
    // This will trigger cleanup for ENDED/ERROR conversations
    onClose?.(false); // false = not closing via back button
    dispatch(close());
    dispatch(resetConversation());
  }, [onClose, dispatch]);

  // Cancel request button - cancel waiting and go back to chat list
  const handleCancelRequest = useCallback(() => {
    console.log('[SupportMessageList] Cancel request clicked');
    dispatch(cancelConversationRequest());
    dispatch(closeChat()); // Navigate back to chat list
  }, [dispatch]);

  // Start new support chat - reset conversation and start fresh without closing modal
  const handleStartNewChat = useCallback(() => {
    console.log('[SupportMessageList] Starting new support chat');
    
    // Validate user context
    if (!salesforceUserContext || !salesforceUserContext.userId) {
      console.error('[SupportMessageList] Cannot start new chat: User context is required');
      return;
    }
    
    // Restart conversation (clears state and stops timer but keeps modal open)
    dispatch(restartConversation());
    
    // Small delay to ensure cleanup completes before starting new conversation
    setTimeout(() => {
      console.log('[SupportMessageList] Starting new Salesforce conversation with routing attributes (fresh start)');
      
      // Build routing attributes with user context
      // Add _skipHistory flag to indicate this is a fresh start after ENDED conversation
      const routing = getRoutingAttributes(salesforceUserContext, {
        ...routingAttributes,
        [ROUTING_ATTRIBUTES.SOURCE]: 'Web_Application',
        [ROUTING_ATTRIBUTES.USER_ID]: authId,
        [ROUTING_ATTRIBUTES.LOCALE]: locale,
        _skipHistory: true // Flag to skip loading history from old conversations
      });
      
      // Start a fresh conversation with proper routing attributes
      dispatch(startSalesforceConversation({ 
        userContext: salesforceUserContext, 
        routingAttributes: routing 
      }));
    }, 500);
  }, [dispatch, salesforceUserContext, routingAttributes, authId, locale]);

  if (!openModal) {
    return null;
  }

  return (
    <Modal
      className={cx("chat-thread chat-type-support", {
        "chat-thread--salesforce": salesforceEnabled,
        "chat-thread--loading": loading,
        "chat-thread--error": error,
      })}
      open={openModal}
      size="small"
      position="right"
      title={messages?.support_title}
      iconBackClose
      handleBack={handleBack}
      useExpandMore
      handleExpand={handleCollapse}
      handleClose={handleClose}
    >

      <ModalBody className="chat-thread-body">
        <div ref={bodyRef} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div ref={scrollRef} id="support-thread-scroll" style={{ flexDirection: items === null || items.length === 0 ? 'column-reverse' : 'column' }} className="chat-thread-messages scrollbar-style">
            <InfiniteScroll
              dataLength={items?.length || 0}
              hasMore={false}
              inverse={true}
              loader={<div />}
              scrollableTarget="support-thread-scroll"
              className="chat-thread-scroller"
              style={{
                flexDirection: items === null || items.length === 0 ? 'column-reverse' : 'column',
                width: "100%",
              }}
            >
              <SupportChatMessages
                items={items}
                authId={authId}
                salesforceEnabled={salesforceEnabled}
                translated={messages}
                chatUsers={chatUsers}
                hasAgentJoined={hasAgentJoined}
                agentJoinedAt={agentJoinedAt}
                agentName={agentName}
                agentJoinedMessage={`${agentName || messages?.support_agent_default_name || 'Support Agent'} ${messages?.support_started_message || 'from GPS Trackit has joined the chat'}`}
              />

              {/* Agent Wait Messages - Only show if no conversations found */}
              {salesforceStatus?.conversationStatus === CONVERSATION_STATUS.WAITING_FOR_AGENT && conversationsCount === 0 && (
                <div className="chat-system-message agent-wait-message">
                  <AgentWaitIcon />
                  <Typography variant="body1" className="wait-message-title">
                    {messages?.support_wait_finishing_title}
                  </Typography>
                  <Typography variant="body2" className="wait-message-text">
                    {messages?.support_wait_finishing_message}
                  </Typography>
                  {!isNetworkOnline && (
                    <Typography variant="body2" className="wait-message-text" style={{ color: '#d32f2f', marginTop: '8px' }}>
                      {messages?.support_network_error || 'Network Connection Issue - Please try again later'}
                    </Typography>
                  )}
                  <a
                    className="cancel-request-link"
                    onClick={handleCancelRequest}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCancelRequest();
                      }
                    }}
                  >
                    {messages?.support_cancel_request}
                  </a>
                </div>
              )}

              {/* Agent Unavailable - Only show if no conversations found */}
              {salesforceStatus?.conversationStatus === CONVERSATION_STATUS.AGENT_UNAVAILABLE && conversationsCount === 0 && (
                <div className="chat-system-message agent-wait-message">
                  <AgentWaitIcon />
                  <Typography variant="body1" className="wait-message-title">
                    {messages?.support_unavailable_title}
                  </Typography>
                  <Typography variant="body2" className="wait-message-text">
                    {messages?.support_unavailable_message_before}
                    <a href="mailto:support@zonarsystems.com" style={{ color: '#00AEEF', display: 'contents', textDecoration: 'underline' }}>
                      {messages?.support_unavailable_email_link}
                    </a>
                    {messages?.support_unavailable_message_after}
                  </Typography>
                  {!isNetworkOnline && (
                    <Typography variant="body2" className="wait-message-text" style={{ color: '#d32f2f', marginTop: '8px' }}>
                      {messages?.support_network_error || 'Network Connection Issue - Please try again later'}
                    </Typography>
                  )}
                  <a
                    className="cancel-request-link"
                    onClick={handleCancelRequest}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCancelRequest();
                      }
                    }}
                  >
                    {messages?.support_cancel_request}
                  </a>
                </div>
              )}

              {/* No Agents Found - Only show if no conversations found */}
              {salesforceStatus?.conversationStatus === CONVERSATION_STATUS.NO_AGENTS_FOUND && conversationsCount === 0 && (
                <div className="chat-system-message agent-wait-message">
                  <AgentWaitIcon />
                  <Typography variant="body1" className="wait-message-title">
                    {messages?.support_no_agents_title}
                  </Typography>
                  <Typography variant="body2" className="wait-message-text">
                    {messages?.support_no_agents_message_before}
                    <a href="mailto:support@zonarsystems.com" style={{ color: '#00AEEF', display: 'contents', textDecoration: 'underline' }}>
                      {messages?.support_unavailable_email_link}
                    </a>
                    {messages?.support_no_agents_message_after}
                  </Typography>
                  {!isNetworkOnline && (
                    <Typography variant="body2" className="wait-message-text" style={{ color: '#d32f2f', marginTop: '8px' }}>
                      {messages?.support_network_error || 'Network Connection Issue - Please try again later'}
                    </Typography>
                  )}
                </div>
              )}

              {salesforceStatus?.isTyping && (
                <div className="chat-typing-indicator">
                  <Typography variant="caption">
                    {messages?.support_agent_typing}
                  </Typography>
                </div>
              )}

              {/* Show "Start a new conversation" when agent is connected but no messages yet */}
              {salesforceStatus?.conversationStatus === CONVERSATION_STATUS.ACTIVE && items !== null && items.length === 0 && (
                <div className="chat-thread-started" style={{ marginBottom: 12 }}>
                  <span className="chat-thread-system-label">
                    {messages?.chat_startNewConversation || "Start a new conversation"}
                  </span>
                </div>
              )}
            </InfiniteScroll>
          </div>
        </div>
      </ModalBody>

      {/* Hide footer when showing waiting screen in body (no conversations found) */}
      {!((conversationsCount === 0) && (
        salesforceStatus?.conversationStatus === CONVERSATION_STATUS.WAITING_FOR_AGENT ||
        salesforceStatus?.conversationStatus === CONVERSATION_STATUS.AGENT_UNAVAILABLE ||
        salesforceStatus?.conversationStatus === CONVERSATION_STATUS.NO_AGENTS_FOUND
      )) && (
      <ModalFooter className="chat-thread-footer">
          {salesforceStatus?.conversationStatus === CONVERSATION_STATUS.ENDED ? (
            <div className="chat-conversation-ended">
              <InputBase style={{display: 'none'}} ref={inputRef} />
              <Typography variant="body2" className="chat-ended-message">
                {`${agentName || messages?.support_agent_default_name || 'Support Agent'} ${messages?.support_ended_message_before || 'from Zonar has left the chat'}`}
              </Typography>
              <a
                className="start-new-chat-link"
                onClick={handleStartNewChat}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleStartNewChat();
                  }
                }}
              >
                {messages?.support_start_new_chat || 'Start a new Support Chat'}
              </a>
            </div>
          ) : salesforceStatus?.conversationStatus === CONVERSATION_STATUS.WAITING_FOR_AGENT ? (
            <div className="chat-waiting-for-routing" style={{borderTop: 'none', padding: 0}}>
              <InputBase style={{display: 'none'}} ref={inputRef} />
              <Typography variant="h6" className="routing-title" style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#333' }}>
                {messages?.support_wait_finishing_title}
              </Typography>
              <Typography variant="body2" className="routing-message" style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                {messages?.support_wait_finishing_message}
              </Typography>
              {!isNetworkOnline && (
                <Typography variant="body2" style={{ fontSize: '14px', color: '#d32f2f', lineHeight: '1.5', marginTop: '8px' }}>
                  {messages?.support_network_error || 'Network Connection Issue - Please try again later'}
                </Typography>
              )}
            </div>
          ) : salesforceStatus?.conversationStatus === CONVERSATION_STATUS.AGENT_UNAVAILABLE ? (
            <div className="chat-waiting-for-routing" style={{borderTop: 'none', padding: 0}}>
              <InputBase style={{display: 'none'}} ref={inputRef} />
              <Typography variant="h6" className="routing-title" style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#333' }}>
                {messages?.support_unavailable_title}
              </Typography>
              <Typography variant="body2" className="routing-message" style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                {messages?.support_unavailable_message_before}
                <a href="mailto:support@zonarsystems.com" style={{ color: '#00AEEF', display: 'contents', textDecoration: 'underline' }}>
                  {messages?.support_unavailable_email_link}
                </a>
                {messages?.support_unavailable_message_after}
              </Typography>
              {!isNetworkOnline && (
                <Typography variant="body2" style={{ fontSize: '14px', color: '#d32f2f', lineHeight: '1.5', marginTop: '8px' }}>
                  {messages?.support_network_error || 'Network Connection Issue - Please try again later'}
                </Typography>
              )}
            </div>
          ) : salesforceStatus?.conversationStatus === CONVERSATION_STATUS.NO_AGENTS_FOUND ? (
            <div className="chat-waiting-for-routing" style={{borderTop: 'none', padding: 0}}>
              <InputBase style={{display: 'none'}} ref={inputRef} />
              <Typography variant="h6" className="routing-title" style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#333' }}>
                {messages?.support_no_agents_title}
              </Typography>
              <Typography variant="body2" className="routing-message" style={{ fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                {messages?.support_no_agents_message_before}
                <a href="mailto:support@zonarsystems.com" style={{ color: '#00AEEF', display: 'contents', textDecoration: 'underline' }}>
                  {messages?.support_unavailable_email_link}
                </a>
                {messages?.support_no_agents_message_after}
              </Typography>
              {!isNetworkOnline && (
                <Typography variant="body2" style={{ fontSize: '14px', color: '#d32f2f', lineHeight: '1.5', marginTop: '8px' }}>
                  {messages?.support_network_error || 'Network Connection Issue - Please try again later'}
                </Typography>
              )}
            </div>
          ) : (
          <div className="chat-composer">
            <div
              className={`chat-composer-input-container ${showInputCharacterCounter ? 'multiline' : 'oneline'} ${(isDisableChatBox()) ? 'disabled' : ''}`}
            >
              <InputBase
                id="support-chat-input"
                className="chat-composer-input scrollbar-style"
                placeholder={inputPlaceholder}
                multiline
                ref={inputRef}
                value={draft}
                disabled={isDisableChatBox()}
                autoFocus
                onKeyDown={(event) => {
                  if (event.key !== "Enter" || isDisableSendMessage()) return;
                  event.preventDefault();
                  handleSubmitMessage();
                }}
                maxRows={6}
                onChange={handleTyping}
              />
              {showInputCharacterCounter && <div
                className={`length-indicator ${draft.length > INPUT_MAX_LENGTH ? 'error' : ''}`}>
                {draft.length}/{INPUT_MAX_LENGTH}
              </div>}
            </div>
            <IconButton
              className={cx("chat-composer-submit chat-type-support", {
                "chat-composer-submit--salesforce": salesforceEnabled,
                "chat-composer-submit--disabled": !canSendMessage,
              })}
              aria-label="submit"
              onClick={handleSubmitMessage}
              disabled={isDisableSendMessage()}
            >
              <NearMeOutlined />
            </IconButton>
          </div>
          )}
        </ModalFooter>
      )}
    </Modal>
  );
};

SupportMessageList.propTypes = {
  messages: PropTypes.object,
  openModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  chat: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  onSendMessage: PropTypes.func,
  onUpdateMessageStatus: PropTypes.func,
  onUserStatusWriting: PropTypes.func,
  authId: PropTypes.string.isRequired,
  chatUsers: PropTypes.arrayOf(PropTypes.object),
  salesforceEnabled: PropTypes.bool,
  salesforceStatus: PropTypes.shape({
    isInitialized: PropTypes.bool,
    isReady: PropTypes.bool,
    conversationStatus: PropTypes.string,
    connectionStatus: PropTypes.string,
    canSendMessage: PropTypes.bool,
    isTyping: PropTypes.bool,
  }),
  loading: PropTypes.bool,
  error: PropTypes.string,
  locale: PropTypes.string,
  routingAttributes: PropTypes.object,
};

export default SupportMessageList;
